import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Bot, CalendarClock, CheckCircle2, Download, FileText, HelpCircle, Loader2, LogOut, Send, ShieldCheck, Upload, XCircle } from "lucide-react";
import { API, api, formatApiError, streamRoleAgentChat } from "../lib/api";

export default function RoleAgentPortal() {
    const { token } = useParams();
    const [state, setState] = useState("loading");
    const [agent, setAgent] = useState(null);
    const [consent, setConsent] = useState(false);
    const [loginEmail, setLoginEmail] = useState("");
    const [loginBusy, setLoginBusy] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [docFile, setDocFile] = useState(null);
    const [docCategory, setDocCategory] = useState("befattningsbeskrivning");
    const [docDesc, setDocDesc] = useState("");
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [mode, setMode] = useState("chat");
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [confirmWithdraw, setConfirmWithdraw] = useState(false);
    const endRef = useRef(null);

    const load = async () => {
        const r = await api.get("/rollagent/session");
        setAgent(r.data);
        setMessages(r.data.messages || []);
        setManualOpen((r.data.messages || []).length <= 1);
        setState("ok");
    };

    useEffect(() => {
        (async () => {
            try {
                if (token) {
                    await api.post(`/rollagent/${token}/session`);
                    window.history.replaceState({}, "", "/rollagent");
                }
                await load();
            } catch (e) {
                setState(e.response?.status === 410 ? "expired" : "login");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const requestLoginLink = async () => {
        if (!loginEmail.includes("@") || loginBusy) return;
        setLoginBusy(true);
        try {
            const r = await api.post("/rollagent/request-login-link", { email: loginEmail.trim() });
            toast.success(r.data.detail || "Kolla din inkorg");
            setState("linkSent");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte skicka inloggningslänken"));
        } finally {
            setLoginBusy(false);
        }
    };

    const logout = async () => {
        try {
            await api.post("/rollagent/session/logout");
        } catch { /* sessionen kan redan vara ogiltig */ }
        setAgent(null);
        setMessages([]);
        setState("login");
        toast.success("Du är utloggad – sessionen är återkallad");
    };
    useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

    const accept = async () => {
        if (!consent) return;
        try {
            const r = await api.post("/rollagent/session/consent", { samtycke: true });
            setAgent(r.data);
            setMessages(r.data.messages || []);
            toast.success("Tack – kontakten är igång");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte registrera samtycket"));
        }
    };

    const send = async () => {
        const text = input.trim();
        if (!text || streaming) return;
        if (mode === "konsult") {
            setStreaming(true);
            try {
                const r = await api.post("/rollagent/session/to-consultant", { meddelande: text });
                setMessages((m) => [...m, r.data.message, r.data.ack]);
                setInput("");
                toast.success("Meddelandet har skickats till konsulten");
            } catch (e) {
                toast.error(formatApiError(e, "Kunde inte skicka meddelandet"));
            } finally {
                setStreaming(false);
            }
            return;
        }
        const userId = `local-user-${Date.now()}`;
        const assistantId = `local-assistant-${Date.now()}`;
        setMessages((m) => [...m,
            { id: userId, role: "user", kind: mode, content: text, at: new Date().toISOString() },
            { id: assistantId, role: "assistant", kind: mode, content: "", at: new Date().toISOString() },
        ]);
        setInput("");
        setStreaming(true);
        try {
            await streamRoleAgentChat(text, mode, (delta) => {
                setMessages((list) => list.map((m) => m.id === assistantId ? { ...m, content: m.content + delta } : m));
            });
            await load();
        } catch (e) {
            setMessages((list) => list.filter((m) => m.id !== assistantId || m.content));
            toast.error(e.message || "AI-agenten kunde inte svara");
        } finally {
            setStreaming(false);
        }
    };

    const withdraw = async () => {
        try {
            await api.post("/rollagent/session/withdraw");
            setState("expired");
        } catch (e) {
            toast.error(formatApiError(e, "Kunde inte avsluta deltagandet"));
        }
    };

    const uploadDoc = async () => {
        if (!docFile || uploading) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", docFile);
            fd.append("kategori", docCategory);
            fd.append("beskrivning", docDesc);
            await api.post("/rollagent/session/documents", fd);
            setDocFile(null);
            setDocDesc("");
            if (fileRef.current) fileRef.current.value = "";
            await load();
            toast.success("Dokumentet har laddats upp – tack!");
        } catch (e) {
            toast.error(formatApiError(e, "Uppladdningen misslyckades"));
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-h-screen canvas-bg text-slate-100" data-testid="role-agent-portal">
            <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
                <header className="flex items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <img src="/hamn-emblem.png" alt="HAMN" className="w-11 h-11 rounded-full border border-current/40 object-cover" />
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Digital rollspegling</p>
                            <h1 className="font-display font-extrabold text-2xl tracking-tight">{agent?.role_label || "Din rollagent"}</h1>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {agent && <span className="font-mono text-[10px] uppercase tracking-widest text-buoy bg-buoy/10 border border-buoy/30 rounded-full px-3 py-1">{agent.days_left} dagar kvar</span>}
                        {state === "ok" && (
                            <button data-testid="participant-logout-button" onClick={logout} title="Logga ut"
                                className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-slate-400 hover:text-red-300 hover:border-red-500/40 transition-colors">
                                <LogOut className="w-3.5 h-3.5" /> Logga ut
                            </button>
                        )}
                    </div>
                </header>

                {state === "loading" && <div className="glass-panel rounded-2xl p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-current" /></div>}
                {state === "login" && (
                    <section className="glass-panel rounded-2xl p-6 sm:p-8 max-w-lg mx-auto" data-testid="participant-login-panel">
                        <ShieldCheck className="w-9 h-9 text-buoy mb-4" />
                        <h2 className="font-display font-bold text-2xl">Logga in i din portal</h2>
                        <p className="text-sm text-slate-400 leading-relaxed mt-3">
                            Ange den e-postadress som fick inbjudan. Vi skickar en ny säker inloggningslänk –
                            av säkerhetsskäl ersätter den tidigare länkar. Hittar du din gamla länk kan du även använda den direkt.
                        </p>
                        <input data-testid="participant-login-email-input" type="email" value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") requestLoginLink(); }}
                            placeholder="din.epost@organisation.se"
                            className="mt-5 w-full bg-abyss/60 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-current/60" />
                        <button data-testid="participant-login-submit-button" onClick={requestLoginLink} disabled={loginBusy || !loginEmail.includes("@")}
                            className="mt-4 w-full rounded-lg bg-current text-abyss font-semibold py-3 hover:bg-tide disabled:opacity-40 flex items-center justify-center gap-2">
                            {loginBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Skicka inloggningslänk
                        </button>
                    </section>
                )}
                {state === "linkSent" && (
                    <section className="glass-panel rounded-2xl p-6 sm:p-8 max-w-lg mx-auto text-center" data-testid="participant-login-sent">
                        <CheckCircle2 className="w-9 h-9 text-buoy mx-auto mb-4" />
                        <h2 className="font-display font-bold text-2xl">Kolla din inkorg</h2>
                        <p className="text-sm text-slate-400 leading-relaxed mt-3">
                            Om adressen har ett aktivt deltagande har vi skickat en ny inloggningslänk.
                            Länken kommer från HAMN ATLAS och leder alltid till denna portal.
                        </p>
                    </section>
                )}
                {state === "expired" && (
                    <div className="glass-panel rounded-2xl p-8 text-center" data-testid="role-agent-expired">
                        <XCircle className="w-9 h-9 text-amber-400 mx-auto mb-3" />
                        <h2 className="font-display font-bold text-xl">Kontaktperioden är avslutad</h2>
                        <p className="text-sm text-slate-400 mt-2">Länken är ogiltig, avslutad eller har löpt ut. Kontakta konsulten om du behöver en ny.</p>
                    </div>
                )}

                {state === "ok" && agent?.krav_samtycke && (
                    <section className="glass-panel rounded-2xl p-6 sm:p-8" data-testid="participant-consent-panel">
                        <ShieldCheck className="w-9 h-9 text-buoy mb-4" />
                        <h2 className="font-display font-bold text-2xl">Samtycke före start</h2>
                        <div className="text-sm text-slate-300 leading-relaxed mt-4 space-y-3">
                            <p>Du har bjudits in att prata med en digital rollagent under kartläggningen av <span className="text-slate-100">{agent.project_name}</span>. Agenten hjälper dig beskriva ditt formella uppdrag, ditt faktiska arbete, kontakter, beroenden och sådant som normalt inte syns.</p>
                            <p>Det du skriver sparas i HAMN ATLAS och kan läsas av konsulten som leder kartläggningen. Informationen används som underlag för organisatorisk analys. Kontakten pågår fram till <span className="font-mono text-buoy">{agent.end_at.slice(0, 10)}</span>.</p>
                            <p>Undvik personnummer, hälsouppgifter eller andra känsliga uppgifter som inte behövs. Du kan avsluta deltagandet när som helst.</p>
                        </div>
                        <label className="flex items-start gap-3 text-sm text-slate-200 mt-6 cursor-pointer">
                            <input data-testid="participant-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 accent-emerald-400" />
                            Jag har läst informationen och samtycker till att mina svar och arbetsdagböcker används för konsultens kartläggning.
                        </label>
                        <button data-testid="participant-consent-submit-button" onClick={accept} disabled={!consent}
                            className="mt-6 w-full rounded-lg bg-current text-abyss font-semibold py-3 hover:bg-tide disabled:opacity-40">Starta kontakten med min rollagent</button>
                    </section>
                )}

                {state === "ok" && agent && !agent.krav_samtycke && (
                    <main className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
                        <section className="glass-panel rounded-2xl flex flex-col min-h-[65vh]" data-testid="ai-companion-chat">
                            <div className="border-b border-border p-4">
                                <div className="flex items-center gap-2"><Bot className="w-5 h-5 text-current" /><h2 className="font-display font-bold text-lg">Din digitala följeslagare</h2></div>
                                <p className="text-xs text-slate-500 mt-1">Samtala om rollen eller skriv arbetsdagbok över vad som faktiskt hände.</p>
                            </div>
                            {agent.unread_consultant_replies > 0 && (
                                <div className="mx-4 mt-4 rounded-xl border border-buoy/40 bg-buoy/10 p-3" data-testid="consultant-reply-banner">
                                    <p className="text-xs text-buoy flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Du har nytt svar från konsulten – se tråden nedan.</p>
                                </div>
                            )}
                            {agent.diary_due && (
                                <div className="mx-4 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3" data-testid="diary-due-banner">
                                    <p className="text-xs text-amber-200 flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Det är dags för en enkel arbetsdagbok – vad gjorde du faktiskt, och vad syntes inte i rollbeskrivningen?</p>
                                </div>
                            )}
                            <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-3">
                                {messages.map((m) => (
                                    <div key={m.id} data-testid={`participant-message-${m.id}`} className={`rounded-xl border p-3 ${m.role === "consultant" ? "border-buoy/40 bg-buoy/10" : m.role === "assistant" ? "border-current/20 bg-current/5" : m.kind === "dagbok" ? "border-amber-500/30 bg-amber-500/5" : m.kind === "till_konsult" ? "border-violet-500/30 bg-violet-500/5" : "border-border bg-deepsea/40"}`}>
                                        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">{m.role === "consultant" ? "Konsulten" : m.role === "assistant" && (m.kind === "proaktiv_fraga" || m.kind === "proaktiv_dagbok") ? "Rollagent · hör av sig" : m.role === "assistant" ? "Rollagent" : m.kind === "dagbok" ? "Arbetsdagbok" : m.kind === "till_konsult" ? "Du → konsulten" : "Du"}</p>
                                        <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{m.content}{streaming && m.content === "" && "…"}</p>
                                    </div>
                                ))}
                                <div ref={endRef} />
                            </div>
                            <div className="border-t border-border p-4">
                                <div className="flex gap-2 mb-3 flex-wrap">
                                    <button data-testid="mode-chat-button" onClick={() => setMode("chat")} className={`rounded-full border px-3 py-1 text-xs ${mode === "chat" ? "border-current/50 text-current bg-current/10" : "border-border text-slate-500"}`}>Samtal</button>
                                    <button data-testid="mode-diary-button" onClick={() => setMode("dagbok")} className={`rounded-full border px-3 py-1 text-xs ${mode === "dagbok" ? "border-amber-400/50 text-amber-300 bg-amber-500/10" : "border-border text-slate-500"}`}>Arbetsdagbok</button>
                                    <button data-testid="mode-consultant-button" onClick={() => setMode("konsult")} className={`rounded-full border px-3 py-1 text-xs ${mode === "konsult" ? "border-violet-400/50 text-violet-300 bg-violet-500/10" : "border-border text-slate-500"}`}>Till konsulten</button>
                                </div>
                                <textarea data-testid={mode === "dagbok" ? "daily-log-text-area" : mode === "konsult" ? "consultant-message-input" : "ai-chat-input"} rows="3" value={input} onChange={(e) => setInput(e.target.value)}
                                    placeholder={mode === "dagbok" ? "Beskriv dagens faktiska arbete, osynliga insatser och kontakter …" : mode === "konsult" ? "Skriv direkt till konsulten – frågor, funderingar eller något du vill lyfta …" : "Skriv till din rollagent …"}
                                    className="w-full bg-abyss/60 border border-border rounded-xl px-3 py-2.5 text-sm outline-none focus:border-current/60" />
                                <button data-testid={mode === "dagbok" ? "daily-log-submit-button" : mode === "konsult" ? "consultant-message-submit" : "ai-chat-send-button"} onClick={send} disabled={streaming || !input.trim()}
                                    className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg bg-current text-abyss font-semibold py-2.5 hover:bg-tide disabled:opacity-40">
                                    {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Skicka
                                </button>
                            </div>
                        </section>

                        <aside className="space-y-4">
                            <section className="glass-panel rounded-2xl p-4" data-testid="participant-manual">
                                <button data-testid="manual-toggle" onClick={() => setManualOpen(!manualOpen)}
                                    className="w-full flex items-center justify-between text-left">
                                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                                        <HelpCircle className="w-3.5 h-3.5" /> Så använder du portalen
                                    </span>
                                    <span className="text-slate-500 text-xs">{manualOpen ? "−" : "+"}</span>
                                </button>
                                {manualOpen && (
                                    <div className="mt-3 space-y-3 text-xs leading-relaxed">
                                        <div>
                                            <p className="font-semibold text-current">Samtal – när du vill prata</p>
                                            <p className="text-slate-400 mt-0.5">Prata fritt med din rollagent: ställ frågor, be om stöd eller resonera om din vardag. Agenten svarar direkt och kan ställa konsultens följdfrågor.</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-amber-300">Arbetsdagbok – när dagen är slut</p>
                                            <p className="text-slate-400 mt-0.5">Skriv kort vad du faktiskt gjort. Viktigast är det som inte syns i rollbeskrivningen: att hjälpa kollegor, lösa problem och informella kontakter. Agenten påminner dig med jämna mellanrum.</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-violet-300">Till konsulten – när du vill nå en människa</p>
                                            <p className="text-slate-400 mt-0.5">Frågor om kartläggningen, integriteten eller något du vill att konsulten ska veta. Konsulten svarar här i portalen.</p>
                                        </div>
                                        <div>
                                            <p className="font-semibold text-buoy">Dokument – underlag om din roll</p>
                                            <p className="text-slate-400 mt-0.5">Ladda upp din befattningsbeskrivning, organisationsbeskrivning eller annat som hjälper konsulten förstå din roll. Max 10 MB per fil.</p>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <section className="glass-panel rounded-2xl p-4" data-testid="participant-documents-panel">
                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" /> Dokument {(agent.documents || []).length > 0 && `(${agent.documents.length})`}
                                </p>
                                {(agent.documents || []).length > 0 && (
                                    <div className="space-y-1.5 mb-3">
                                        {agent.documents.map((d) => (
                                            <a key={d.id} data-testid={`document-download-${d.id}`}
                                                href={`${API}/rollagent/session/documents/${d.id}/download`}
                                                className="flex items-center gap-2 rounded-lg border border-border bg-abyss/40 px-2.5 py-2 hover:border-current/40 transition-colors">
                                                <Download className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                                                <span className="text-[11px] text-slate-300 truncate">{d.filename}</span>
                                                <span className="font-mono text-[9px] text-slate-600 shrink-0 ml-auto">{Math.max(1, Math.round(d.size / 1024))} kB</span>
                                            </a>
                                        ))}
                                    </div>
                                )}
                                <input ref={fileRef} type="file" className="hidden" data-testid="document-file-input"
                                    accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.xlsx,.csv"
                                    onChange={(e) => setDocFile(e.target.files?.[0] || null)} />
                                <div className="space-y-2">
                                    <select data-testid="document-category-select" value={docCategory} onChange={(e) => setDocCategory(e.target.value)}
                                        className="w-full bg-abyss/60 border border-border rounded-lg px-2.5 py-2 text-xs outline-none">
                                        <option value="befattningsbeskrivning">Befattningsbeskrivning</option>
                                        <option value="organisationsbeskrivning">Organisationsbeskrivning</option>
                                        <option value="schema">Schema / arbetsordning</option>
                                        <option value="annat">Annat underlag</option>
                                    </select>
                                    <input data-testid="document-description-input" value={docDesc} onChange={(e) => setDocDesc(e.target.value)}
                                        placeholder="Kort beskrivning (valfritt)"
                                        className="w-full bg-abyss/60 border border-border rounded-lg px-2.5 py-2 text-xs outline-none" />
                                    <button data-testid="document-choose-button" onClick={() => fileRef.current?.click()}
                                        className="w-full rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-slate-400 hover:border-current/50 hover:text-slate-200 transition-colors">
                                        {docFile ? `Vald fil: ${docFile.name}` : "Välj fil (pdf, word, bild, excel – max 10 MB)"}
                                    </button>
                                    <button data-testid="document-upload-button" onClick={uploadDoc} disabled={!docFile || uploading}
                                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-buoy/15 border border-buoy/40 text-buoy py-2 text-xs font-semibold hover:bg-buoy/25 disabled:opacity-40">
                                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Ladda upp
                                    </button>
                                </div>
                            </section>
                            <section className="glass-panel rounded-2xl p-4">
                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Period</p>
                                <p className="text-sm text-slate-300">Kontakten pågår till <span className="font-mono text-buoy">{agent.end_at.slice(0, 10)}</span>.</p>
                                <p className="text-xs text-slate-500 mt-2">Dagbok efterfrågas ungefär var {agent.diary_interval_days}:e dag.</p>
                            </section>
                            <section className="glass-panel rounded-2xl p-4">
                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Konsultens frågor</p>
                                <div className="space-y-2">{agent.prepared_questions.map((q, i) => <p key={i} className="text-xs text-slate-300">· {q}</p>)}</div>
                            </section>
                            <section className="glass-panel rounded-2xl p-4">
                                <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-2">Din kontroll</p>
                                {!confirmWithdraw ? (
                                    <button data-testid="participant-withdraw-button" onClick={() => setConfirmWithdraw(true)} className="w-full rounded-lg border border-red-500/40 text-red-300 py-2 text-xs hover:bg-red-500/10">Avsluta mitt deltagande</button>
                                ) : (
                                    <button data-testid="participant-withdraw-confirm" onClick={withdraw} className="w-full rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 py-2 text-xs font-semibold">Bekräfta avslut</button>
                                )}
                            </section>
                        </aside>
                    </main>
                )}
            </div>
        </div>
    );
}
